/**
 * PublishEditorState — Selection and interaction state for the Publish Editor
 *
 * sl-9qm.3.3: Centralized state for component selection, hover, and drag
 * within the Publish Editor's canvas. Consumed by the shell and slot components.
 *
 * SOLID:
 * - S: Only manages editor interaction state (not layout, not data)
 */
import type { PreviewSourceMark } from '@streamlain/view-config';
import { LiveData, Service } from '@toeverything/infra';

export interface DragState {
  /** Component type being dragged (from palette) */
  componentType: string;
  /** Current mouse position */
  x: number;
  y: number;
}

/** Studio chrome or fullscreen file-app preview. */
export type EditorMode = 'edit' | 'preview';

/**
 * sl-bpu6.8: Responsive-test viewport. `desktop` leaves the canvas
 * full-width; `tablet`/`mobile` constrain the canvas width so authors
 * can check responsive behaviour without resizing the browser.
 */
import type { ViewportSize } from '@streamlain/app-flow-ui/canvas/preview-devices';

export type { ViewportSize };

/**
 * What the App Studio stage shows. Every mode renders inside the same studio
 * frame (navigation, header, AI composer); only the stage content changes:
 *   - `studio`       → live preview with optional inspector panel. Default.
 *   - `translations` → language configuration and quality overview.
 *   - `repair`       → schema-validation surface for the AppBase DBs.
 *
 * The URL-param `?mode=translations|repair` is the source of truth;
 * the service mirrors it bidirectionally so deep links land directly and
 * switches inside the editor stay in sync with the browser URL. The default
 * `studio` mode clears the param.
 */
export type EditorViewMode = 'studio' | 'translations' | 'repair';

export interface AppBasePublication {
  readonly isLive: boolean;
  /** The draft differs from the live version. */
  readonly isDirty: boolean;
  readonly liveUrl: string | null;
}

/**
 * App Studio panels: flow shows the shared Flow workspace on the stage;
 * content/access/seo/versions/usage sit beside the live preview, agent-actions
 * replaces it in the work area. Publish readiness is checked in
 * the publish dialog, not in a panel. Apps are built from artifact code files;
 * the legacy page builder ("structure") is intentionally not a panel
 * (2026-10-08).
 */
export const STUDIO_PANELS = [
  'flow',
  'content',
  'access',
  'seo',
  'versions',
  'usage',
  'agent-actions',
  'tables',
] as const;
export type StudioPanel = (typeof STUDIO_PANELS)[number];

const STUDIO_PANEL_SET: ReadonlySet<string> = new Set(STUDIO_PANELS);

/** Reads a `?panel=` value; unknown or missing values mean "closed". */
export function parseStudioPanel(value: string | null): StudioPanel | null {
  return value !== null && STUDIO_PANEL_SET.has(value)
    ? (value as StudioPanel)
    : null;
}

/**
 * sl-sbux.7: Wizard step persistence. The wizard owns its step labels and
 * help copy, but the active step is held here so that mode switches
 * (Studio → Repair → Studio) restore the same step without re-mounting
 * the wizard state.
 */
export type WizardStepId =
  | 'pages'
  | 'structure'
  | 'content'
  | 'preview'
  | 'publish';

/**
 * sl-sbux.7: Optional banner navigation request. When the wizard surfaces
 * a fail-loud banner ("open Repair view") the click writes an issue-id
 * into this LiveData; the RepairView consumes it to scroll/highlight the
 * referenced issue and resets the field once the user acknowledges.
 */
export interface RepairNavigationRequest {
  readonly issueId: string;
  readonly requestedAt: number;
}

export interface WizardSectionStatus {
  readonly done: boolean;
  readonly issueCount: number;
}

/**
 * The entry, and the field of it, the author works on in the content area.
 * The preview shows a frame around it.
 */
export interface StudioContentFocus {
  readonly sourceKey: string;
  readonly rowId: string;
  /** `null` marks the whole entry. */
  readonly columnId: string | null;
  /** Finished status text of the entry, shown at the frame. */
  readonly status: string | null;
}

/** An entry the author picked inside the preview. */
export interface StudioContentPick {
  readonly sourceKey: string;
  readonly rowId: string;
  readonly columnId: string | null;
  /** The picked place shows plain text and can take typing. */
  readonly inline: boolean;
}

/** One field of one entry, as the preview shows it. */
export interface StudioContentPlace {
  readonly sourceKey: string;
  readonly rowId: string;
  readonly columnId: string;
}

/** What the author typed into a place of the preview: its whole text. */
export interface StudioContentInput extends StudioContentPlace {
  readonly text: string;
  /** The author has left the place. */
  readonly done: boolean;
  /** The author typed past what a place on the page takes. */
  readonly full?: true;
}

export type WizardSectionStatusMap = Readonly<
  Partial<Record<WizardStepId, WizardSectionStatus>>
>;

export class PublishEditorState extends Service {
  /** Currently selected component in the canvas */
  readonly selectedComponentId$ = new LiveData<string | null>(null);

  /**
   * Monotonic selection intent counter. Re-selecting the same component must
   * still reopen the inspector after a user has visited another sidebar tab;
   * `selectedComponentId$` alone de-duplicates equal values.
   */
  readonly selectionRequestVersion$ = new LiveData<number>(0);

  /** Currently hovered component in the canvas */
  readonly hoveredComponentId$ = new LiveData<string | null>(null);

  /** Active drag operation (from palette to canvas) */
  readonly dragState$ = new LiveData<DragState | null>(null);

  /** sl-bpu6.8: edit vs preview chrome */
  readonly editorMode$ = new LiveData<EditorMode>('edit');

  /** Route selected in the file-app preview. */
  readonly activePreviewPageId$ = new LiveData<string | null>(null);

  /** sl-bpu6.8: simulated viewport width for responsive test */
  readonly viewport$ = new LiveData<ViewportSize>('desktop');

  /** Device chosen for the tablet and the phone preview; unset = default. */
  readonly previewDevices$ = new LiveData<
    Partial<Record<Exclude<ViewportSize, 'desktop'>, string>>
  >({});

  /**
   * sl-pbux.8.4: builder reduced-motion simulation. When `true`, the canvas
   * applies `reduced-motion` style variants unconditionally (and motion
   * presets honour the policy as if `prefers-reduced-motion: reduce` were
   * set). `false` leaves the system preference in charge.
   */
  readonly simulatedReducedMotion$ = new LiveData<boolean>(false);

  /** App Studio stage mode (studio | translations | repair) */
  readonly editorViewMode$ = new LiveData<EditorViewMode>('studio');

  /** App Studio inspector panel beside the live preview; null = closed. */
  readonly studioPanel$ = new LiveData<StudioPanel | null>(null);

  /**
   * Publication state of the open AppBase, reported by the publish control in
   * the top bar (its single owner); null until the first load has finished.
   */
  readonly publication$ = new LiveData<AppBasePublication | null>(null);

  /** Counts requests to open the publish check from outside the top bar. */
  readonly publishRequestVersion$ = new LiveData<number>(0);

  /** sl-sbux.7: persisted wizard step so mode switches stay verlustfrei */
  readonly wizardStep$ = new LiveData<WizardStepId>('structure');

  /** sl-sbux.7: set of step ids the user reached at least once */
  readonly wizardReachedSteps$ = new LiveData<ReadonlySet<WizardStepId>>(
    new Set<WizardStepId>(['structure'])
  );

  /** sl-sbux.7: pending repair-view focus request (issue id + tick) */
  readonly repairNavigationRequest$ =
    new LiveData<RepairNavigationRequest | null>(null);

  /**
   * sl-sbux.7: live count of schema errors surfaced by Reparatur. Drives the
   * Reparatur-tab badge in the top-bar mode switch so users see open issues
   * without a wizard banner that interrupts the active step.
   */
  readonly schemaErrorCount$ = new LiveData<number>(0);

  /** Errors that block publishing, including database-health failures. */
  readonly blockingIssueCount$ = new LiveData<number>(0);

  /** Non-blocking structural warnings shown by review and navigation. */
  readonly warningIssueCount$ = new LiveData<number>(0);

  private structuralIssueCount = 0;

  private structuralWarningCount = 0;

  private databaseHealthIssueCount = 0;
  /** Compact setup completeness shown beside the persistent app navigation. */
  readonly wizardSectionStatus$ = new LiveData<WizardSectionStatusMap>({});

  /** Entry and field the author works on in the content area. */
  readonly contentFocus$ = new LiveData<StudioContentFocus | null>(null);

  /** Entry picked in the preview, until the content area has opened it. */
  readonly contentPickRequest$ = new LiveData<StudioContentPick | null>(null);

  /** Place of the preview the author may type into, with the value of its cell, as the content area allowed it. */
  readonly contentEditGrant$ = new LiveData<{
    place: StudioContentPlace;
    text: string;
  } | null>(null);

  /** Latest input the author typed into the preview. */
  readonly contentInput$ = new LiveData<StudioContentInput | null>(null);

  setContentFocus(focus: StudioContentFocus | null): void {
    const current = this.contentFocus$.value;
    if (
      current?.sourceKey === focus?.sourceKey &&
      current?.rowId === focus?.rowId &&
      current?.columnId === focus?.columnId &&
      current?.status === focus?.status
    )
      return;
    this.contentFocus$.next(focus);
  }

  /** Opens the content area on the entry the author picked in the preview. */
  requestContentPick(pick: StudioContentPick): void {
    this.contentPickRequest$.next(pick);
    this.setStudioPanel('content');
  }

  clearContentPick(): void {
    if (this.contentPickRequest$.value === null) return;
    this.contentPickRequest$.next(null);
  }

  /** Lets the author type into the place they picked in the preview. */
  grantContentEdit(place: StudioContentPlace, text: string): void {
    this.contentEditGrant$.next({ place: { ...place }, text });
  }

  reportContentInput(input: StudioContentInput): void {
    this.contentInput$.next(input);
  }

  /** Document whose presence carries the pointers of this App Container. */
  readonly previewPointerDocId$ = new LiveData<string | null>(null);

  setPreviewPointerDoc(docId: string | null): void {
    if (this.previewPointerDocId$.value === docId) return;
    this.previewPointerDocId$.next(docId);
  }

  /** Tables of the shown page the preview frames, each in its colour. */
  readonly previewSourceMarks$ = new LiveData<readonly PreviewSourceMark[]>([]);

  setPreviewSourceMarks(marks: readonly PreviewSourceMark[]): void {
    if (
      JSON.stringify(this.previewSourceMarks$.value) === JSON.stringify(marks)
    )
      return;
    this.previewSourceMarks$.next(marks);
  }

  /** The table the author points at in the access dialog. */
  readonly previewSourceFocus$ = new LiveData<string | null>(null);

  setPreviewSourceFocus(sourceKey: string | null): void {
    if (this.previewSourceFocus$.value === sourceKey) return;
    this.previewSourceFocus$.next(sourceKey);
  }

  /** A table the author clicked in the preview while the access dialog is open. */
  readonly accessTableRequest$ = new LiveData<string | null>(null);

  requestAccessTable(sourceKey: string): void {
    this.accessTableRequest$.next(sourceKey);
  }

  clearAccessTableRequest(): void {
    if (this.accessTableRequest$.value === null) return;
    this.accessTableRequest$.next(null);
  }

  /** Page of the entry in work, for the preview to open. */
  readonly contentEntryPath$ = new LiveData<string | null>(null);

  showContentEntryPage(path: string | null): void {
    if (this.contentEntryPath$.value !== path)
      this.contentEntryPath$.next(path);
  }

  /**
   * Language the content pane works in. The preview page shows that language,
   * so text typed on the page is the text of the cell in work.
   */
  readonly contentLocale$ = new LiveData<string | null>(null);

  showContentLocale(locale: string | null): void {
    if (this.contentLocale$.value !== locale) this.contentLocale$.next(locale);
  }

  /** Fields of the entry in work that the preview page takes typing for. */
  readonly contentPlaces$ = new LiveData<readonly StudioContentPlace[]>([]);

  reportContentPlaces(places: readonly StudioContentPlace[]): void {
    if (places.length === 0 && this.contentPlaces$.value.length === 0) return;
    this.contentPlaces$.next(places.map(place => ({ ...place })));
  }

  select(componentId: string | null): void {
    this.selectionRequestVersion$.next(this.selectionRequestVersion$.value + 1);
    this.selectedComponentId$.next(componentId);
  }

  hover(componentId: string | null): void {
    this.hoveredComponentId$.next(componentId);
  }

  startDrag(componentType: string, x: number, y: number): void {
    this.dragState$.next({ componentType, x, y });
  }

  updateDrag(x: number, y: number): void {
    const current = this.dragState$.value;
    if (!current) return;
    this.dragState$.next({ ...current, x, y });
  }

  endDrag(): void {
    this.dragState$.next(null);
  }

  setEditorMode(mode: EditorMode): void {
    this.editorMode$.next(mode);
    // Leaving edit mode clears selection — preview has no slot affordances.
    if (mode === 'preview') {
      this.selectedComponentId$.next(null);
      this.hoveredComponentId$.next(null);
    }
  }

  setActivePreviewPage(pageId: string | null): void {
    if (this.activePreviewPageId$.value === pageId) return;
    this.activePreviewPageId$.next(pageId);
    this.select(null);
  }

  setViewport(viewport: ViewportSize): void {
    this.viewport$.next(viewport);
  }

  setPreviewDevice(
    kind: Exclude<ViewportSize, 'desktop'>,
    deviceId: string
  ): void {
    if (this.previewDevices$.value[kind] === deviceId) return;
    this.previewDevices$.next({
      ...this.previewDevices$.value,
      [kind]: deviceId,
    });
  }

  setSimulatedReducedMotion(enabled: boolean): void {
    this.simulatedReducedMotion$.next(enabled);
  }

  setEditorViewMode(mode: EditorViewMode): void {
    if (this.editorViewMode$.value === mode) return;
    this.editorViewMode$.next(mode);
  }

  setStudioPanel(panel: StudioPanel | null): void {
    if (this.studioPanel$.value === panel) return;
    this.studioPanel$.next(panel);
  }

  setPublication(publication: AppBasePublication | null): void {
    const current = this.publication$.value;
    if (
      current === publication ||
      (current &&
        publication &&
        current.isLive === publication.isLive &&
        current.isDirty === publication.isDirty &&
        current.liveUrl === publication.liveUrl)
    ) {
      return;
    }
    this.publication$.next(publication);
  }

  /** Ask the top bar to open the publish check. */
  requestPublish(): void {
    this.publishRequestVersion$.next(this.publishRequestVersion$.value + 1);
  }

  setWizardStep(step: WizardStepId): void {
    if (this.wizardStep$.value === step) return;
    this.wizardStep$.next(step);
    const reached = this.wizardReachedSteps$.value;
    if (reached.has(step)) return;
    const next = new Set<WizardStepId>(reached);
    next.add(step);
    this.wizardReachedSteps$.next(next);
  }

  requestRepairFocus(issueId: string): void {
    this.repairNavigationRequest$.next({
      issueId,
      requestedAt: Date.now(),
    });
  }

  clearRepairFocus(): void {
    this.repairNavigationRequest$.next(null);
  }

  setSchemaErrorCount(count: number): void {
    this.structuralIssueCount = Math.max(0, count);
    this.structuralWarningCount = 0;
    this.publishCombinedIssueCount();
  }

  setStructuralIssueCounts(errors: number, warnings: number): void {
    this.structuralIssueCount = Math.max(0, errors);
    this.structuralWarningCount = Math.max(0, warnings);
    this.publishCombinedIssueCount();
  }

  setDatabaseHealthIssueCount(count: number): void {
    this.databaseHealthIssueCount = Math.max(0, count);
    this.publishCombinedIssueCount();
  }

  resetIssueCounts(): void {
    this.structuralIssueCount = 0;
    this.structuralWarningCount = 0;
    this.databaseHealthIssueCount = 0;
    this.publishCombinedIssueCount();
  }

  private publishCombinedIssueCount(): void {
    const blocking = this.structuralIssueCount + this.databaseHealthIssueCount;
    const warnings = this.structuralWarningCount;
    const count = blocking + warnings;
    if (this.blockingIssueCount$.value !== blocking) {
      this.blockingIssueCount$.next(blocking);
    }
    if (this.warningIssueCount$.value !== warnings) {
      this.warningIssueCount$.next(warnings);
    }
    if (this.schemaErrorCount$.value === count) return;
    this.schemaErrorCount$.next(count);
  }

  setWizardSectionStatus(status: WizardSectionStatusMap): void {
    this.wizardSectionStatus$.next(status);
  }

  /** Reset all state (e.g., when leaving publish mode) */
  reset(): void {
    this.selectedComponentId$.next(null);
    this.selectionRequestVersion$.next(0);
    this.hoveredComponentId$.next(null);
    this.dragState$.next(null);
    this.editorMode$.next('edit');
    this.activePreviewPageId$.next(null);
    this.viewport$.next('desktop');
    this.simulatedReducedMotion$.next(false);
    this.editorViewMode$.next('studio');
    this.studioPanel$.next(null);
    this.contentFocus$.next(null);
    this.contentPickRequest$.next(null);
    this.contentEditGrant$.next(null);
    this.contentInput$.next(null);
    this.contentPlaces$.next([]);
    this.contentLocale$.next(null);
    this.contentEntryPath$.next(null);
    this.publication$.next(null);
    this.wizardStep$.next('structure');
    this.wizardReachedSteps$.next(new Set<WizardStepId>(['structure']));
    this.repairNavigationRequest$.next(null);
    this.resetIssueCounts();
    this.wizardSectionStatus$.next({});
  }
}
